import styled from '@emotion/styled';
import { Tooltip, TooltipProps } from '@mui/material';
import {
    getTinyVariantBackground,
    getTinyVariantColor,
    getTinyVariantHoverBackground,
    getTinyVariantHoverColor,
    TinyVariant,
} from '@shared/constants/components/tinyUtils';
import { ButtonHTMLAttributes, forwardRef } from 'react';
import { IconName } from '../images/IconMap';
import Icons from '../images/Icons';
import { MixinFocusVisible } from '../structure/DefaultStyles';

interface ITinyIconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    icon: IconName;
    label: string;
    tooltip?: string;
    tooltipPlacement?: TooltipProps['placement'];
    iconSize?: number;
    iconWeight?: string;
    variant?: TinyVariant;
}

/**
 * The small square icon-only button shared by the panel and AI History surfaces (copy, stop, clear
 * selection, feedback votes). Toggle state is read from `aria-pressed` rather than a style prop so the
 * pressed appearance and the state screen readers announce can never drift apart. Forwards its ref so
 * MUI's Tooltip can anchor to it — its own `tooltip` prop included. Colors come from the same variants as
 * the rest of the tiny family, defaulting to the muted accent tone these buttons have always rested at.
 */
export const TinyIconButton = forwardRef<HTMLButtonElement, ITinyIconButtonProps>(function TinyIconButton(
    {
        icon,
        label,
        tooltip,
        tooltipPlacement,
        iconSize = 1,
        iconWeight = 'bold',
        variant = 'diminished_accent',
        ...buttonProps
    },
    ref
) {
    const tag = (
        <TinyIconButtonBase ref={ref} type="button" aria-label={label} $variant={variant} {...buttonProps}>
            <Icons name={icon} size={iconSize} weight={iconWeight} />
        </TinyIconButtonBase>
    );

    if (!tooltip) return tag;

    return (
        <Tooltip title={tooltip} placement={tooltipPlacement} arrow>
            {tag}
        </Tooltip>
    );
});

const TinyIconButtonBase = styled.button<{ $variant: TinyVariant }>`
    width: 1.625rem;
    height: 1.625rem;
    flex: 0 0 auto;
    padding: 0;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${(props) => getTinyVariantColor(props.$variant, props.theme) || props.theme.color.text};
    background: ${(props) => getTinyVariantBackground(props.$variant, props.theme) || 'transparent'};
    transition:
        background 0.15s ease,
        border-color 0.15s ease,
        color 0.15s ease;

    /* Hover and the pressed toggle share one look: the variant's color and background trade places. */
    &[aria-pressed='true'],
    &:hover:not(:disabled) {
        background: ${(props) => getTinyVariantHoverBackground(props.$variant, props.theme)};
        color: ${(props) => getTinyVariantHoverColor(props.$variant, props.theme)};
        border-color: currentColor;
    }

    ${MixinFocusVisible}

    &:disabled {
        cursor: default;
        opacity: 0.45;
    }

    /* A recorded vote disables its button but must still read as the chosen one. */
    &[aria-pressed='true']:disabled {
        opacity: 1;
    }
`;
